/* eslint-disable @typescript-eslint/no-explicit-any */
import { motion, MotionProps } from 'framer-motion'
import cn from 'classnames'
import React, { memo, ReactNode, useMemo } from 'react'

export interface FieldProps {
  motionProps?: MotionProps
  className?: string
  children: React.ReactElement<
    {
      name: string
      className: string
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
      value: any
      onBlur: VoidFunction
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
      onSelect: (event: React.FocusEvent<HTMLInputElement>) => void
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
      onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => void
      type: HTMLInputElement['type']
      format?: string
    },
    any
  >
  invalid?: boolean
  helpText?: ReactNode
  label?: ReactNode
  required?: boolean | ReactNode
  requirementDecoration?: ReactNode
  orientation?: 'vertical' | 'horizontal' | 'grid'
}

const Field = memo(
  ({
    children,
    className = '',
    motionProps = {},
    helpText,
    label,
    required = false,
    requirementDecoration = <span className="text-danger">*</span>,
    orientation = 'horizontal',
    invalid = false
  }: FieldProps) => {
    const inputName: string = useMemo(() => {
      const name = children.props?.name
      if (label && !name) {
        throw new Error(
          '<Field /> requires a child with `name` prop if a label is provided'
        )
      }
      return name
    }, [children, label])

    const isSwitchOrCheckbox =
      ['checkbox'].includes(children.props.type) ||
      ['Checkbox', 'Switch'].includes(children.type.displayName) ||
      typeof children.props.value === 'boolean'

    const orientationClassNames = useMemo(() => {
      return {
        content: {
          vertical: 'flex-col',
          horizontal: isSwitchOrCheckbox ? 'items-center' : 'items-baseline',
          grid: cn([
            'grid grid-cols-none md:grid-cols-12 mb-6',
            helpText ? 'items-start' : 'items-center'
          ])
        },
        labelString: {
          vertical: 'mb-2',
          horizontal: isSwitchOrCheckbox ? 'order-last ml-2' : 'mr-2',
          grid: cn([
            'mr-2 mb-3 md:mb-0 md:text-right col-span-4 font-normal !text-base',
            helpText && !isSwitchOrCheckbox && 'mt-2'
          ])
        },
        labelElement: {
          vertical: '!mb-2',
          horizontal: isSwitchOrCheckbox ? 'order-last ml-2' : 'mr-2',
          grid: cn([
            'mr-2 mb-3 md:mb-0 md:text-right col-span-4',
            helpText && !isSwitchOrCheckbox && 'mt-2'
          ])
        },
        inputContainer: {
          vertical: isSwitchOrCheckbox ? '' : 'w-full',
          horizontal: isSwitchOrCheckbox ? '' : 'w-full',
          grid: isSwitchOrCheckbox && !helpText ? '' : 'col-span-7'
        },
        inputElement: {
          vertical: '',
          horizontal: '',
          grid: children.props?.className?.includes('input-') ? '' : 'w-full'
        }
      }
    }, [children.props?.className, helpText, isSwitchOrCheckbox])

    const alteredChild = useMemo(() => {
      return {
        ...children,
        props: {
          ...children.props,
          value: isSwitchOrCheckbox
            ? !!children?.props?.value
            : children?.props?.value,
          invalid: invalid ? 'true' : undefined,
          className: cn([
            orientationClassNames.inputElement[orientation],
            children.props?.className
          ])
        }
      }
    }, [
      orientationClassNames.inputElement,
      children,
      invalid,
      isSwitchOrCheckbox,
      orientation
    ])

    return (
      <motion.div
        className={cn([
          'mb-4 flex',
          orientationClassNames.content[orientation],
          className
        ])}
        {...motionProps}
      >
        {label && (
          <>
            {typeof label === 'string' ? (
              <label
                className={cn([
                  'text-sm whitespace-nowrap',
                  invalid && 'text-danger-0',
                  orientationClassNames.labelString[orientation]
                ])}
                htmlFor={inputName}
              >
                {label} {required && requirementDecoration}
              </label>
            ) : (
              <div
                className={cn(
                  orientationClassNames.labelElement[orientation],
                  'whitespace-nowrap'
                )}
              >
                {label}
                {required && requirementDecoration}
              </div>
            )}
          </>
        )}
        <div
          className={cn([
            'leading-none',
            orientationClassNames.inputContainer[orientation]
          ])}
        >
          {alteredChild}
          {helpText && typeof helpText === 'string' ? (
            <p
              className={cn([
                'text-sm mt-2 text-dark-3',
                invalid && 'text-danger-0'
              ])}
            >
              {helpText}
            </p>
          ) : (
            helpText
          )}
        </div>
      </motion.div>
    )
  }
)

export default Field
